import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { useClient } from "../../contexts/ClientContext";
import AuthCard from "./AuthCard";
import { getLoginErrorKey } from "./helpers";

function SignInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn } = useClient();

  const [values, setValues] = useState({ email: "", password: "" });
  const [errorKey, setErrorKey] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorKey("");
    setIsSubmitting(true);

    const response = await signIn(values);
    setIsSubmitting(false);

    if (!response.success) {
      setErrorKey(getLoginErrorKey(response.error));
      return;
    }

    navigate("/", { replace: true });
  };

  return (
    <AuthCard
      title={t("auth.signInTitle")}
      subtitle={t("auth.signInDescription")}
    >
      <Stack component="form" spacing={2} onSubmit={handleSubmit}>
        <TextField
          required
          type="email"
          label={t("auth.emailLabel")}
          value={values.email}
          onChange={(event) =>
            setValues((prev) => ({ ...prev, email: event.target.value }))
          }
        />
        <TextField
          required
          type={showPassword ? "text" : "password"}
          label={t("auth.passwordLabel")}
          value={values.password}
          onChange={(event) =>
            setValues((prev) => ({ ...prev, password: event.target.value }))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("auth.togglePasswordVisibility")}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {errorKey ? <Typography color="error">{t(errorKey)}</Typography> : null}

        <Button component={RouterLink} to="/forgot-password">
          {t("auth.forgotPasswordLink")}
        </Button>

        <Button type="submit" variant="contained" disabled={isSubmitting}>
          {isSubmitting ? t("common.loading") : t("auth.signInButton")}
        </Button>
        <Button component={RouterLink} to="/create-member">
          {t("auth.goToCreateMember")}
        </Button>
      </Stack>
    </AuthCard>
  );
}

export default SignInPage;
